import React, { useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import LimitReachedModal from "../LimitReachedModal";
import { X, Wand2, Loader2, RotateCw, Upload, Check } from "lucide-react";

const GENRE_HINTS = {
  "Children's Educational": "A bright, playful illustrated scene of children doing maths, yellow and purple colour scheme, flat illustration style.",
  "Self-Help & Personal Development": "Clean, aspirational, modern composition with soft natural light. Minimalist symbolism — a path, a door, or a horizon.",
  "Business & Entrepreneurship": "Confident, modern, geometric composition with bold typography space. Deep blue and gold accents. Subtle abstract shapes.",
  "Health & Wellness": "Calm, organic, natural light, botanical elements, sage green and warm cream palette. Soft focus.",
  "Religious & Christian": "Reverent, uplifting composition with soft golden light, subtle cross or dove motif if appropriate, warm and hopeful palette. Clean negative space for typography. Faith-based without being heavy-handed — feels contemporary and welcoming.",
  "Bible Study & Devotional": "Open Bible or aged parchment texture with warm candlelight, sepia and gold palette, quiet contemplative mood, classical and timeless.",
  "Inspirational / Faith Fiction": "Soft pastoral landscape at dawn, single figure in silhouette, hopeful warm light breaking through clouds, gentle and emotive.",
  "Spiritual & New Age": "Ethereal, sacred geometry or natural mandala, soft glowing light, deep indigo and gold palette, mystical and tranquil.",
  "Journals & Low-Content": "Elegant minimalist composition, subtle botanical or geometric motif, calming palette, generous negative space for title overlay. Bookstore-quality refined feel.",
  "Fiction – Thriller": "Dark, cinematic, dramatic lighting. Moody palette, single haunting object as focal point. Heavy shadows and grain.",
  "Fiction – Romance": "Warm, soft golden light, intimate composition, dreamy bokeh, blush and burgundy palette.",
  "Fiction – Historical": "Painterly, vintage texture, sepia tones, ornate detail, classical composition with depth.",
  "Cookbooks & Food": "Rustic overhead shot, natural ingredients, warm wooden surface, soft window light, vibrant produce.",
  "Biographies & Memoirs": "Single iconic portrait, archival texture, monochromatic palette, classical and timeless.",
  "Other": "Striking, original concept with strong central focus, professional book cover composition.",
};

const AIImagePanel = ({ onClose, onAccept, onUpload }) => {
  const { profile, refreshProfile } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [genre, setGenre] = useState("");
  const [provider, setProvider] = useState("flux");
  const [loading, setLoading] = useState(false);
  // Session history — every successful generation pushes onto this array
  // so the user can swap between variations without losing earlier ones.
  // [{ src, prompt, provider, ts }]  most-recent-first.
  const [history, setHistory] = useState([]);
  // Index of the variation currently shown in the big preview. 0 = newest.
  const [activeIdx, setActiveIdx] = useState(0);
  const [error, setError] = useState("");
  const [limitInfo, setLimitInfo] = useState(null);

  const generate = async () => {
    if (!prompt.trim()) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/cover/generate-image", { prompt, provider });
      const entry = {
        src: data.image_data_url || data.image_url,
        prompt,
        provider: data.provider || provider,
        ts: Date.now(),
      };
      setHistory((h) => [entry, ...h].slice(0, 12)); // cap at 12 to bound memory
      setActiveIdx(0);
      refreshProfile();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "limit_reached") {
        setLimitInfo({ type: detail.type || "ai_image", plan: detail.plan || profile?.plan || "free" });
      } else {
        setError(typeof detail === "string" ? detail : "Generation failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  const active = history[activeIdx];

  const applyGenreHint = (g) => {
    setGenre(g);
    if (g && GENRE_HINTS[g]) setPrompt(GENRE_HINTS[g]);
  };

  const aiUsed = profile?.ai_images_used_this_month || 0;
  const aiLimit = profile?.plan === "pro" ? 30 : profile?.plan === "starter" ? 10 : 1;

  return (
    <div className="fixed inset-y-0 right-0 z-40 w-[460px] bg-white border-l border-[#D3D1C7] shadow-2xl flex flex-col" data-testid="ai-image-panel">
      <div className="px-5 py-4 border-b border-[#D3D1C7] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wand2 className="w-4 h-4 text-[#534AB7]" />
          <h3 className="text-sm font-medium text-[#2C2C2A]" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
            Generate with AI
          </h3>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A]" aria-label="Close panel">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <div>
          <label className="block text-xs font-medium text-[#5F5E5A] uppercase tracking-wider mb-1.5">AI engine</label>
          <div className="grid grid-cols-2 gap-1.5" data-testid="ai-provider-toggle">
            <button
              type="button"
              onClick={() => setProvider("flux")}
              data-testid="ai-provider-flux"
              className={`px-3 py-2 rounded-[8px] text-xs font-medium border transition-colors ${
                provider === "flux"
                  ? "bg-[#534AB7] text-white border-[#534AB7]"
                  : "bg-white text-[#2C2C2A] border-[#D3D1C7] hover:bg-[#F1EFE8]"
              }`}
            >
              FLUX 1.1 Pro
              <span className="block text-[10px] opacity-80 mt-0.5 font-normal">Fal.ai · photoreal</span>
            </button>
            <button
              type="button"
              onClick={() => setProvider("dalle")}
              data-testid="ai-provider-dalle"
              className={`px-3 py-2 rounded-[8px] text-xs font-medium border transition-colors ${
                provider === "dalle"
                  ? "bg-[#534AB7] text-white border-[#534AB7]"
                  : "bg-white text-[#2C2C2A] border-[#D3D1C7] hover:bg-[#F1EFE8]"
              }`}
            >
              GPT Image 1
              <span className="block text-[10px] opacity-80 mt-0.5 font-normal">OpenAI · illustrative</span>
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#5F5E5A] uppercase tracking-wider mb-1.5">Genre helper</label>
          <select
            value={genre}
            onChange={(e) => applyGenreHint(e.target.value)}
            data-testid="ai-genre-select"
            className="w-full px-3 py-2 rounded-[8px] bg-white border border-[#D3D1C7] text-sm focus:outline-none focus:border-[#534AB7]"
          >
            <option value="">— Choose a genre to auto-fill prompt —</option>
            {Object.keys(GENRE_HINTS).map((g) => <option key={g}>{g}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#5F5E5A] uppercase tracking-wider mb-1.5">Prompt</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={5}
            data-testid="ai-prompt-input"
            placeholder="Describe the image you want — style, mood, colours, subject. E.g. 'A bright, playful illustrated scene of children doing maths, yellow and purple colour scheme, flat design style'"
            className="w-full px-3 py-2 rounded-[8px] bg-white border border-[#D3D1C7] text-sm focus:outline-none focus:border-[#534AB7] resize-none"
          />
        </div>

        {error && <p className="text-sm text-[#BA7517]" data-testid="ai-error">{error}</p>}

        <button
          onClick={generate}
          disabled={!prompt.trim() || loading}
          data-testid="ai-generate-btn"
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] disabled:opacity-50 transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          {loading ? "Generating your cover image…" : "Generate image"}
        </button>

        <p className="text-xs text-[#5F5E5A] text-center" data-testid="ai-usage-counter">
          {aiUsed} of {aiLimit} AI generations used this month
        </p>

        {active && (
          <div className="border border-[#D3D1C7] rounded-[12px] overflow-hidden" data-testid="ai-result">
            <img
              key={active.ts}
              src={active.src}
              alt="AI cover"
              className="w-full"
            />
            <div className="px-3 py-2 flex items-center justify-between bg-[#FAFAF9] border-t border-[#D3D1C7]">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#5F5E5A]">
                Variation {history.length - activeIdx} of {history.length}
              </span>
              <span className="text-[10px] text-[#5F5E5A]">{active.provider}</span>
            </div>
            <div className="p-3 grid grid-cols-2 gap-2 bg-[#FAFAF9]">
              <button
                onClick={() => onAccept(active.src)}
                data-testid="ai-use-image-btn"
                className="px-3 py-2 rounded-md bg-[#534AB7] text-white text-xs font-medium hover:bg-[#3C3489] flex items-center justify-center gap-1.5"
              >
                Use this image
              </button>
              <button
                onClick={generate}
                disabled={loading}
                data-testid="ai-try-again-btn"
                className="px-3 py-2 rounded-md border border-[#D3D1C7] text-xs hover:bg-[#F1EFE8] flex items-center justify-center gap-1.5"
              >
                <RotateCw className="w-3 h-3" /> Generate another
              </button>
            </div>
          </div>
        )}

        {history.length > 1 && (
          <div data-testid="ai-history-strip">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#5F5E5A]">
                Session variations ({history.length})
              </span>
              <span className="text-[10px] text-[#5F5E5A] italic">click to compare</span>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {history.map((h, i) => (
                <button
                  key={h.ts}
                  onClick={() => setActiveIdx(i)}
                  data-testid={`ai-history-${i}`}
                  className={`relative aspect-[2/3] rounded-md overflow-hidden border-2 transition-all ${
                    i === activeIdx ? "border-[#534AB7] shadow-md" : "border-[#D3D1C7] hover:border-[#534AB7]/60 opacity-80 hover:opacity-100"
                  }`}
                  title={`Generated ${new Date(h.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
                >
                  <img src={h.src} alt="" className="w-full h-full object-cover" />
                  {i === activeIdx && (
                    <span className="absolute top-1 right-1 bg-[#534AB7] text-white rounded-full w-4 h-4 flex items-center justify-center">
                      <Check className="w-2.5 h-2.5" />
                    </span>
                  )}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[10px] text-[#5F5E5A] italic">
              Variations are kept for this session only. Once you close this panel, they're gone.
            </p>
          </div>
        )}

        <button
          onClick={onUpload}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-[8px] border border-[#D3D1C7] text-xs hover:bg-[#F1EFE8]"
          data-testid="ai-upload-instead-btn"
        >
          <Upload className="w-3 h-3" /> Upload my own instead
        </button>
      </div>
      <LimitReachedModal
        open={!!limitInfo}
        type={limitInfo?.type || "ai_image"}
        plan={limitInfo?.plan || "free"}
        onClose={() => setLimitInfo(null)}
      />
    </div>
  );
};

export default AIImagePanel;
